import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  FolderOpen,
  FileText,
  ChevronRight,
  ChevronDown,
  Mail,
  MessageSquare,
  Book,
  AlertTriangle,
  Import,
  Search,
  Eye,
  Loader,
  Library,
  Filter,
  CheckCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

import surveyService from '../../services/surveyService';
import reminderService from '../../services/reminderService';

// A helper to parse + sanitize Markdown
const parseMarkdown = (markdown = '') => {
  const rawHtml = marked(markdown);
  return DOMPurify.sanitize(rawHtml);
};

/**
 * LibraryImportModal allows browsing the global library and importing items to a specific study.
 */
const LibraryImportModal = ({
  isOpen,
  onClose,
  studyName,
  currentStudyId,
  currentStudyDraftId,
  onImportComplete
}) => {
  // State for library items
  const [standardSurveys, setStandardSurveys] = useState([]);
  const [missingForms, setMissingForms] = useState([]);
  const [emailReminders, setEmailReminders] = useState([]);
  const [pushReminders, setPushReminders] = useState([]);

  // State for filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // State for loading
  const [loading, setLoading] = useState({
    surveys: false,
    missingForms: false,
    emailReminders: false,
    pushReminders: false,
    importing: false
  });

  // State for expanded items and preview
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Fetch all library items when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAllLibraryItems();
    }
  }, [isOpen]);

  // Fetch all global library items
  const fetchAllLibraryItems = async () => {
    fetchGlobalStandardSurveys();
    fetchGlobalMissingForms();
    fetchGlobalEmailReminders();
    fetchGlobalPushReminders();
  };

  // Fetch standard surveys
  const fetchGlobalStandardSurveys = async () => {
    setLoading(prev => ({ ...prev, surveys: true }));
    try {
      const response = await surveyService.getGlobalSurveys('standard');
      setStandardSurveys(response.data || []);
    } catch (error) {
      console.error('Error fetching global standard surveys:', error);
      toast.error('Failed to load global standard surveys');
    } finally {
      setLoading(prev => ({ ...prev, surveys: false }));
    }
  };

  // Fetch missing forms
  const fetchGlobalMissingForms = async () => {
    setLoading(prev => ({ ...prev, missingForms: true }));
    try {
      const response = await surveyService.getGlobalSurveys('missing_form');
      setMissingForms(response.data || []);
    } catch (error) {
      console.error('Error fetching global missing forms:', error);
      toast.error('Failed to load global missing forms');
    } finally {
      setLoading(prev => ({ ...prev, missingForms: false }));
    }
  };

  // Fetch email reminders
  const fetchGlobalEmailReminders = async () => {
    setLoading(prev => ({ ...prev, emailReminders: true }));
    try {
      const response = await reminderService.getGlobalReminders('email');
      setEmailReminders(response.data || []);
    } catch (error) {
      console.error('Error fetching global email reminders:', error);
      toast.error('Failed to load global email reminders');
    } finally {
      setLoading(prev => ({ ...prev, emailReminders: false }));
    }
  };

  // Fetch push reminders
  const fetchGlobalPushReminders = async () => {
    setLoading(prev => ({ ...prev, pushReminders: true }));
    try {
      const response = await reminderService.getGlobalReminders('push');
      setPushReminders(response.data || []);
    } catch (error) {
      console.error('Error fetching global push reminders:', error);
      toast.error('Failed to load global push reminders');
    } finally {
      setLoading(prev => ({ ...prev, pushReminders: false }));
    }
  };

  // Toggle item expansion
  const toggleItemExpansion = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Open preview for an item
  const handlePreview = (item, itemType) => {
    setSelectedItem({ ...item, itemType });
    setPreviewOpen(true);
  };

  // Import item to study
  const handleImport = async (item, itemType) => {
    if (!currentStudyId || !studyName) {
      toast.error('Study information is missing. Unable to import.');
      return;
    }

    setLoading(prev => ({ ...prev, importing: true }));

    try {
      let response;

      // Check for duplicate names before importing surveys
      if (itemType === 'standard_survey' || itemType === 'missing_form') {
        // Fetch existing surveys in the study to check for duplicates
        const surveyResponse = await surveyService.getSurveysByStudyId(studyName);
        const existingSurveys = surveyResponse?.surveys || [];
        
        const duplicateName = existingSurveys.find(existingSurvey => 
          existingSurvey.name?.toLowerCase() === item.name?.toLowerCase()
        );
        
        if (duplicateName) {
          toast.error(`A survey named "${item.name}" already exists in this study. Please rename it before importing or choose a different survey.`);
          setLoading(prev => ({ ...prev, importing: false }));
          return;
        }
        
        response = await surveyService.importGlobalSurveyToStudy(item.id, currentStudyId);
      } else if (itemType === 'email_reminder' || itemType === 'push_reminder') {
        // Check for duplicate reminder names
        if (currentStudyDraftId) {
          const existingReminders = await reminderService.getReminderTemplates(currentStudyDraftId);
          const validReminders = Array.isArray(existingReminders) ? existingReminders : [];
          
          const duplicateName = validReminders.find(existingReminder => 
            existingReminder.name?.toLowerCase() === item.name?.toLowerCase()
          );
          
          if (duplicateName) {
            toast.error(`A reminder template named "${item.name}" already exists in this study. Please rename it before importing or choose a different template.`);
            setLoading(prev => ({ ...prev, importing: false }));
            return;
          }
        }
        
        response = await reminderService.importGlobalReminderToStudy(item.id, currentStudyId);
      }

      toast.success(`Successfully imported ${getItemTypeName(itemType)}`);
      
      // Close preview if open
      if (previewOpen) {
        setPreviewOpen(false);
      }
      
      // Call the onImportComplete callback to refresh the parent's lists
      if (onImportComplete) {
        onImportComplete();
      }
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error(`Error importing ${itemType}:`, error);
      toast.error(`Failed to import ${getItemTypeName(itemType)}`);
    } finally {
      setLoading(prev => ({ ...prev, importing: false }));
    }
  };

  // Get display name for item type
  const getItemTypeName = (itemType) => {
    switch (itemType) {
      case 'standard_survey':
        return 'standard survey';
      case 'missing_form':
        return 'missing form';
      case 'email_reminder':
        return 'email reminder';
      case 'push_reminder':
        return 'push reminder';
      default:
        return 'item';
    }
  };

  // Get icon for item type
  const getItemIcon = (itemType) => {
    switch (itemType) {
      case 'standard_survey':
        return <Book className="w-5 h-5 text-primary-600" />;
      case 'missing_form':
        return <AlertTriangle className="w-5 h-5 text-primary-600" />;
      case 'email_reminder':
        return <Mail className="w-5 h-5 text-primary-600" />;
      case 'push_reminder':
        return <MessageSquare className="w-5 h-5 text-primary-600" />;
      default:
        return <FileText className="w-5 h-5 text-primary-600" />;
    }
  };

  // Get badge color for item type
  const getItemTypeColor = (itemType) => {
    switch (itemType) {
      case 'standard_survey':
        return 'bg-blue-100 text-blue-800';
      case 'missing_form':
        return 'bg-amber-100 text-amber-800';
      case 'email_reminder':
        return 'bg-indigo-100 text-indigo-800';
      case 'push_reminder':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter items based on search query and active category
  const getFilteredItems = () => {
    const query = searchQuery.toLowerCase();
    
    // Get items based on active category
    let items = [];
    
    if (activeCategory === 'all' || activeCategory === 'standard_survey') {
      items = [...items, ...standardSurveys.map(item => ({ ...item, itemType: 'standard_survey' }))];
    }
    
    if (activeCategory === 'all' || activeCategory === 'missing_form') {
      items = [...items, ...missingForms.map(item => ({ ...item, itemType: 'missing_form' }))];
    }
    
    if (activeCategory === 'all' || activeCategory === 'email_reminder') {
      items = [...items, ...emailReminders.map(item => ({ ...item, itemType: 'email_reminder', survey_type: 'email_reminder' }))];
    }
    
    if (activeCategory === 'all' || activeCategory === 'push_reminder') {
      items = [...items, ...pushReminders.map(item => ({ ...item, itemType: 'push_reminder', survey_type: 'push_reminder' }))];
    }
    
    // Filter by search query if provided
    if (query) {
      return items.filter(item => 
        (item.name && item.name.toLowerCase().includes(query)) ||
        (item.description && item.description.toLowerCase().includes(query))
      );
    }
    
    return items;
  };

  // Count items by category
  const getItemCounts = () => {
    return {
      all: standardSurveys.length + missingForms.length + emailReminders.length + pushReminders.length,
      standard_survey: standardSurveys.length,
      missing_form: missingForms.length,
      email_reminder: emailReminders.length,
      push_reminder: pushReminders.length
    };
  };

  const counts = getItemCounts();

  // Render the item preview
  const renderItemPreview = () => {
    if (!selectedItem) return null;

    const { itemType } = selectedItem;

    if (itemType === 'standard_survey' || itemType === 'missing_form') {
      return (
        <div className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{selectedItem.name}</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${getItemTypeColor(itemType)}`}>
              {itemType === 'standard_survey' ? 'Standard Survey' : 'Missing Form'}
            </span>
          </div>
          
          {selectedItem.description && (
            <div className="mb-6 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
              <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-2">Description</h4>
              <p className="text-gray-600 dark:text-gray-300">{selectedItem.description}</p>
            </div>
          )}
          
          {selectedItem.questions && selectedItem.questions.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-3 flex items-center">
                <FileText className="w-4 h-4 mr-2 text-primary-600" />
                Questions ({selectedItem.questions.length})
              </h4>
              <div className="space-y-4">
                {selectedItem.questions.map((question, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="flex justify-between items-start">
                      <p className="font-medium text-gray-800 dark:text-gray-100">{question.text}</p>
                      <span className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-full">
                        {question.type}
                      </span>
                    </div>
                    
                    {question.options && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Options:</p>
                        {question.type === 'rating' ? (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded-md text-sm text-gray-600 dark:text-gray-300">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="font-medium">Min:</span> {question.options.min}
                              </div>
                              <div>
                                <span className="font-medium">Max:</span> {question.options.max}
                              </div>
                            </div>
                            {question.options.labels && (
                              <div className="mt-2">
                                <span className="font-medium">Labels:</span>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {question.options.labels.map((label, idx) => (
                                    <span key={idx} className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md text-xs">
                                      {label}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : Array.isArray(question.options) && question.options.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {question.options.map((option, idx) => (
                              <span key={idx} className="px-2 py-1 bg-white dark:bg-gray-800 rounded-md text-sm border border-gray-200 dark:border-gray-700">
                                {option}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    } else if (itemType === 'email_reminder' || itemType === 'push_reminder') {
      return (
        <div className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{selectedItem.name}</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${getItemTypeColor(itemType)}`}>
              {itemType === 'email_reminder' ? 'Email Reminder' : 'Push Notification'}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
              <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-2">Channel</h4>
              <div className="flex items-center">
                {itemType === 'email_reminder' ? 
                  <Mail className="w-4 h-4 mr-2 text-indigo-600" /> : 
                  <MessageSquare className="w-4 h-4 mr-2 text-purple-600" />
                }
                <span className="text-gray-600 dark:text-gray-300 capitalize">{selectedItem.channel}</span>
              </div>
            </div>
            
            {selectedItem.channel === 'email' && selectedItem.metadata && selectedItem.metadata.subject && (
              <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-2">Subject</h4>
                <p className="text-gray-600 dark:text-gray-300">{selectedItem.metadata.subject}</p>
              </div>
            )}
          </div>
          
          <div className="mb-6">
            <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center">
              <FileText className="w-4 h-4 mr-2 text-primary-600" />
              Template Content
            </h4>
            <div 
              className="text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(selectedItem.template_content) }}
            />
          </div>
          
          {selectedItem.placeholders && (
            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
              <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-3">Placeholders</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedItem.placeholders.required && selectedItem.placeholders.required.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Required:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedItem.placeholders.required.map((placeholder, idx) => (
                        <span key={idx} className="px-2 py-1 bg-primary-100 text-primary-800 rounded-md text-xs">
                          ${placeholder}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedItem.placeholders.optional && selectedItem.placeholders.optional.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Optional:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedItem.placeholders.optional.map((placeholder, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md text-xs">
                          ${placeholder}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }
    
    return <div className="p-6 text-center text-gray-600 dark:text-gray-300">No preview available</div>;
  };

  const isAnyLoading = loading.surveys || loading.missingForms || loading.emailReminders || loading.pushReminders;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', damping: 25 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl dark:shadow-gray-900/20 w-full max-w-5xl max-h-[calc(100vh-40px)] flex flex-col"
          >
            {/* Header */}
            <div className="px-6 py-4 bg-primary-600 text-white rounded-t-xl flex items-center justify-between">
              <div className="flex items-center">
                <Library className="w-6 h-6 mr-3" />
                <h2 className="text-xl font-semibold">Import from Global Library</h2>
              </div>
              <button 
                onClick={onClose} 
                className="text-white p-1 hover:bg-primary-700 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex flex-col md:flex-row h-full overflow-hidden">
              {/* Browser Panel */}
              <div className="w-full md:w-2/5 p-4 border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                <div className="mb-4 flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Library Items</h3>
                  {isAnyLoading && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                      <Loader className="w-4 h-4 animate-spin mr-2 text-primary-600" />
                      Loading...
                    </div>
                  )}
                </div>
                
                {/* Search */}
                <div className="relative mb-4">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search library..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow duration-200 bg-white dark:bg-gray-800 dark:text-gray-100"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Category Filter */}
                <div className="mb-4">
                  <div className="flex items-center mb-2">
                    <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">Categories</h3>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <button
                      className={`w-full text-left px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center ${
                        activeCategory === 'all' ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setActiveCategory('all')}
                    >
                      <span>All Items</span>
                      <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">{counts.all}</span>
                    </button>
                    <button
                      className={`w-full text-left px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center ${
                        activeCategory === 'standard_survey' ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setActiveCategory('standard_survey')}
                    >
                      <span>Standard Surveys</span>
                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">{counts.standard_survey}</span>
                    </button>
                    <button
                      className={`w-full text-left px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center ${
                        activeCategory === 'missing_form' ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setActiveCategory('missing_form')}
                    >
                      <span>Missing Forms</span>
                      <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">{counts.missing_form}</span>
                    </button>
                    <button
                      className={`w-full text-left px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center ${
                        activeCategory === 'email_reminder' ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setActiveCategory('email_reminder')}
                    >
                      <span>Email Reminders</span>
                      <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">{counts.email_reminder}</span>
                    </button>
                    <button
                      className={`w-full text-left px-4 py-2.5 flex justify-between items-center ${
                        activeCategory === 'push_reminder' ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setActiveCategory('push_reminder')}
                    >
                      <span>Push Reminders</span>
                      <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">{counts.push_reminder}</span>
                    </button>
                  </div>
                </div>

                {/* Item List */}
                <div className="space-y-3">
                  {isAnyLoading ? (
                    <div className="py-16 flex flex-col items-center justify-center">
                      <Loader className="w-8 h-8 text-primary-600 animate-spin mb-3" />
                      <p className="text-gray-500 dark:text-gray-400">Loading library items...</p>
                    </div>
                  ) : getFilteredItems().length === 0 ? (
                    <div className="py-16 flex flex-col items-center justify-center bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <Library className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium mb-1">No items found</p>
                      <p className="text-gray-400 dark:text-gray-500 text-sm">
                        {searchQuery ? 'Try a different search term' : 'The selected category is empty'}
                      </p>
                    </div>
                  ) : (
                    getFilteredItems().map(item => (
                      <div key={`${item.itemType}-${item.id}`} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div 
                          className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            selectedItem && selectedItem.id === item.id && selectedItem.itemType === item.itemType ? 
                            'bg-primary-50 border-l-4 border-l-primary-500' : ''
                          }`}
                          onClick={() => toggleItemExpansion(`${item.itemType}-${item.id}`)}
                        >
                          <div className="flex items-center">
                            <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded-md mr-3">
                              {getItemIcon(item.itemType)}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">{item.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {item.itemType === 'standard_survey' && 'Standard Survey'}
                                {item.itemType === 'missing_form' && 'Missing Form'}
                                {item.itemType === 'email_reminder' && 'Email Reminder'}
                                {item.itemType === 'push_reminder' && 'Push Reminder'}
                              </div>
                            </div>
                          </div>
                          {expandedItems[`${item.itemType}-${item.id}`] ? (
                            <ChevronDown className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                          )}
                        </div>
                        
                        {expandedItems[`${item.itemType}-${item.id}`] && (
                          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                            {item.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{item.description}</p>
                            )}
                            <div className="flex space-x-2">
                              <button
                                className="flex-1 flex items-center justify-center px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-200 transition-colors shadow-sm dark:shadow-gray-900/20"
                                onClick={() => handlePreview(item, item.itemType)}
                              >
                                <Eye className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                                Preview
                              </button>
                              <button
                                className="flex-1 flex items-center justify-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors shadow-sm dark:shadow-gray-900/20"
                                onClick={() => handleImport(item, item.itemType)}
                                disabled={loading.importing}
                              >
                                {loading.importing ? (
                                  <>
                                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                                    Importing...
                                  </>
                                ) : (
                                  <>
                                    <Import className="w-4 h-4 mr-2" />
                                    Import
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Preview Panel */}
              <div className="w-full md:w-3/5 flex flex-col overflow-hidden bg-white dark:bg-gray-800">
                {previewOpen && selectedItem ? (
                  <>
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center">
                        <div className="bg-gray-100 dark:bg-gray-800 p-1.5 rounded-md mr-3">
                          {getItemIcon(selectedItem.itemType)}
                        </div>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">
                          Preview: {selectedItem.name}
                        </h3>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          className="flex items-center px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md text-gray-700 dark:text-gray-200 transition-colors shadow-sm dark:shadow-gray-900/20"
                          onClick={() => setPreviewOpen(false)}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Close
                        </button>
                        <button
                          className="flex items-center px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md shadow-sm dark:shadow-gray-900/20 transition-colors"
                          onClick={() => handleImport(selectedItem, selectedItem.itemType)}
                          disabled={loading.importing}
                        >
                          {loading.importing ? (
                            <>
                              <Loader className="w-4 h-4 mr-2 animate-spin" />
                              Importing...
                            </>
                          ) : (
                            <>
                              <Import className="w-4 h-4 mr-2" />
                              Import
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="overflow-y-auto">
                      {renderItemPreview()}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full p-8 text-center">
                    <div className="max-w-sm">
                      <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-200 dark:text-gray-600" />
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No Item Selected</h3>
                      <p className="text-gray-500 dark:text-gray-400 mb-6">
                        Select an item from the list to preview its contents and import it to your study.
                      </p>
                      <div className="flex flex-col space-y-2 text-left bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Tips:</span>
                        <div className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-primary-600 mr-2 mt-0.5" />
                          <span className="text-sm text-gray-600 dark:text-gray-300">Use the search box to find specific items</span>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-primary-600 mr-2 mt-0.5" />
                          <span className="text-sm text-gray-600 dark:text-gray-300">Filter by category to narrow down results</span>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-primary-600 mr-2 mt-0.5" />
                          <span className="text-sm text-gray-600 dark:text-gray-300">Preview items before importing them to your study</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LibraryImportModal;